import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'routes/constants';
import { Kebab, PageHead, Pager } from 'views/design-system';

import { Empty, RuleChips, ScoreCell, StatusChip, TagChips } from '../bits';
import { Spark } from '../charts';
import {
	ago,
	corridor,
	dateLine,
	edgeOf,
	hms,
	feeState,
	feeTitle,
	legsLabel,
	money,
	until,
	verdictOf,
} from '../data/format';
import { IAlert, ITransaction } from '../data/types';
import {
	RECORD_FORMATS,
	useAlertQueue,
	useMonitoringRules,
	useRecentTransactions,
	useTmActions,
	useTransactionReport,
	useTxnStats,
} from '../data/use-tm';
import { TxnDrawer } from './txn-drawer';

import '../../design-system/tokens.scss';
import '../../design-system/surface.scss';
import '../txn-surface.scss';
import './triage-deck.scss';

const SORTS: Record<string, string> = { due: 'Due soonest', score: 'Highest score', new: 'Newest' };
const SEVS = ['All', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const PAGE_SIZE = 40;

/* The bundle filter's fallback list, used only until the catalogue answers.
 *
 * It is NOT the source of truth: the real bundles come from the rule catalogue
 * (`GET /monitoring-rules`), because a hand-typed list drifts the moment a rule
 * ships in a new bundle — which had already happened. `FATF` is in the seeded
 * catalogue and was missing here, so alerts raised by the FATF rule could not be
 * filtered to at all, and the filter looked complete while silently omitting a
 * whole bundle. Deriving it means a new bundle appears the moment a rule uses
 * one. */
const FALLBACK_BUNDLES = [
	'AML/CTF',
	'Anomaly detection',
	'Crypto monitoring',
	'Crypto screening',
	'Custom',
	'Device intelligence',
	'FATF',
	'Finance',
	'Fraud prevention',
	'Travel rule',
];
const TABS: { key: string; label: string; tone?: string }[] = [
	{ key: 'open', label: 'Open', tone: 'warn' },
	{ key: 'investigating', label: 'Investigating' },
	{ key: 'awaiting', label: 'Awaiting user' },
	{ key: 'sar', label: 'Pending SAR' },
	{ key: 'done', label: 'Resolved today' },
	{ key: 'all', label: 'All' },
	{ key: 'mine', label: 'Assigned to me', tone: 'mine' },
];

/**
 * The alert's SLA clock: amber at a quarter left, red once breached.
 *
 * A null `dueAt` carries no class at all. Only HISTORICAL alerts have one, and
 * `alerts.find` excludes those from every tab — but guarded rather than asserted,
 * because `new Date(null)` is the epoch, which would render every such row as
 * fifty-six years overdue and screaming red if that exclusion ever regressed.
 */
const dueClass = (a: IAlert) => {
	if (!a.dueAt) return '';
	const due = new Date(a.dueAt).getTime();
	const total = due - new Date(a.createdAt).getTime();
	const left = due - Date.now();
	return left < 0 ? 'late' : left < total * 0.25 ? 'hot' : '';
};

/* The queue endpoint populates the transaction onto each alert; anywhere it is
   still a bare id the row cannot be drawn, so it is skipped rather than rendered
   with blanks where the subject should be. */
const txnOf = (a: IAlert): ITransaction | null =>
	a.linkedTransactionId && typeof a.linkedTransactionId === 'object'
		? (a.linkedTransactionId as ITransaction)
		: null;

const Tile: FC<{ tone: number; label: string; n: number; note: string; pts: number[] }> = ({
	tone,
	label,
	n,
	note,
	pts,
}) => (
	<div className="tile">
		<div className="l">
			<span className={`mk mk${tone}`} />
			{label}
		</div>
		<div className="n">{n}</div>
		<div className="d">{note}</div>
		<Spark vals={pts} cls={`sp${tone}`} />
	</div>
);

export const TriageDeck: FC = () => {
	const navigate = useNavigate();
	const [tab, setTab] = useState('open');
	const [sort, setSort] = useState('due');
	const [sevIdx, setSevIdx] = useState(0);
	/* Held as the VALUE, not an index. The bundle list is derived from the rule
	   catalogue and so grows when the rules arrive — an index into it would keep
	   pointing at the same slot while the bundle under that slot changed, quietly
	   refiltering the queue by something the analyst never chose. */
	const [bundle, setBundle] = useState('All');
	/* Payment vs trade, as a FILTER and not only as a chip on the row. A chip
	   answers "what is this one?"; a trading-desk client's actual question is
	   "show me every trade", which only a filter answers. Fixed three-value list,
	   so unlike `bundle` an index would be safe — held as the value anyway to
	   match the control beside it rather than mix two idioms in one strip. */
	const [kind, setKind] = useState<'All' | 'Payment' | 'Trade'>('All');
	const [q, setQ] = useState('');
	/* DEEP LINK: `?txn=<id>` opens that transaction's drawer on arrival. A link an
	   analyst can paste to a colleague ("look at this one"), and what the QA
	   suite uses to open an exact transaction instead of hunting for its row —
	   the row may be on another page, or filtered out by the current tab. Read
	   once at mount; the drawer does not write it back, so closing it does not
	   push history entries. */
	const [openId, setOpenId] = useState<string | null>(() => {
		try {
			return new URLSearchParams(window.location.search).get('txn');
		} catch {
			return null;
		}
	});
	const [checked, setChecked] = useState<Set<string>>(new Set());
	const [bulkDlOpen, setBulkDlOpen] = useState(false);
	/* The row the keyboard is on. Separate from `openId`: j/k move the cursor
	   without opening anything, which is the whole point of being able to skim a
	   queue without a drawer covering half of it. */
	const [cursor, setCursor] = useState<string | null>(null);
	const [pageNo, setPageNo] = useState(1);

	/* Any change to what is being asked for sends the queue back to page 1.
	   Without this, narrowing a filter while on page 3 asks for the third page of
	   a result set that may now have one — the service answers with an empty page
	   and the table reads as "nothing in this queue" for a filter that matches
	   plenty. */
	useEffect(() => {
		setPageNo(1);
	}, [tab, sort, sevIdx, bundle, kind, q]);

	const filters = useMemo(
		() => ({
			tab,
			sort,
			severity: sevIdx ? SEVS[sevIdx] : '',
			bundle: bundle === 'All' ? '' : bundle,
			transactionKind: kind === 'All' ? '' : kind.toLowerCase(),
			q,
			pageNo,
			pageSize: PAGE_SIZE,
		}),
		[tab, sort, sevIdx, bundle, kind, q, pageNo]
	);

	const { queue, loading, failed, reload } = useAlertQueue(filters);
	const { stats, failed: statsFailed } = useTxnStats(60);
	const tape = useRecentTransactions(12);
	const { decide, updateAlert, bulk } = useTmActions();
	const { download, busy: downloading } = useTransactionReport();
	/* Only for the bundle list and the Rules button's count — tuning lives on the
	   rules page now, so nothing here writes to the catalogue. */
	const { rules } = useMonitoringRules();

	/* The bundle filter is the catalogue's own bundles, not a list typed here —
	   see FALLBACK_BUNDLES. Sorted so the order does not shift as rules load. */
	const bundles = useMemo(() => {
		const found = Array.from(new Set(rules.map(r => r.bundle))).sort();
		return ['All', ...(found.length ? found : FALLBACK_BUNDLES)];
	}, [rules]);

	/* Memoised because `?? []` builds a NEW array whenever the queue is null, and
	   runBulk depends on it — an unmemoised default makes that callback change
	   identity on every render for as long as the first fetch is in flight. */
	const rows = useMemo(() => queue?.data ?? [], [queue]);
	const counts = queue?.counts;
	const total = queue?.total ?? 0;
	const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const act = useCallback(
		async (txnId: string, status: string, reasonCode?: string) => {
			await decide(txnId, status, reasonCode);
			reload();
		},
		[decide, reload]
	);

	const alertAct = useCallback(
		async (alertId: string, body: Record<string, unknown>) => {
			await updateAlert(alertId, body);
			reload();
		},
		[updateAlert, reload]
	);

	/* Selection is keyed by ALERT id, but approve/decline act on the
	   TRANSACTION — so a bulk decision resolves each alert back to its
	   transaction first. Rows whose transaction did not populate are dropped
	   rather than sent as `undefined`, which the service would reject one at a
	   time with no indication which row was at fault. */
	const runBulk = useCallback(
		async (what: 'approve' | 'decline' | 'dismiss' | 'me') => {
			const ids = Array.from(checked);
			if (what === 'me') {
				await bulk(ids, id => updateAlert(id, { assignToMe: true }));
			} else {
				const txnIds = ids
					.map(id => {
						const row = rows.find(a => a._id === id);
						return row ? txnOf(row)?._id : null;
					})
					.filter((x): x is string => !!x);
				const [status, code] =
					what === 'approve'
						? ['APPROVED', 'analyst_approved']
						: what === 'decline'
							? ['DECLINED', 'analyst_declined']
							: ['APPROVED', 'analyst_cleared'];
				await bulk(txnIds, id => decide(id, status as string, code as string));
			}
			setChecked(new Set());
			reload();
		},
		[checked, rows, bulk, updateAlert, decide, reload]
	);

	/* The transactions behind the current selection, resolved the same way
	   `runBulk` resolves them — selection is keyed by alert id and a record is a
	   document about a TRANSACTION. A selected row whose transaction did not
	   populate is dropped rather than sent as `undefined`, so the count on the
	   button is the number of records that will actually be produced. */
	const selectedTxnIds = useMemo(
		() =>
			Array.from(checked)
				.map(id => {
					const row = rows.find(a => a._id === id);
					return row ? txnOf(row)?._id : null;
				})
				.filter((x): x is string => !!x),
		[checked, rows]
	);

	const toggle = useCallback((id: string, on: boolean) => {
		setChecked(prev => {
			const next = new Set(prev);
			if (on) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);

	/* Keyboard triage. The handler is on `document` because the queue is a table
	   of rows, not a focusable list — binding to the table would need a row to be
	   focused first, which defeats "land on the page and start working".
	   Deliberately inert while typing: the search box and the drawer's note field
	   are both plain inputs, and `a` meaning "approve" while someone types a
	   comment would be a decision nobody made. */
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const el = e.target as HTMLElement | null;
			if (el && el.closest('input, textarea, select, [contenteditable="true"]')) return;
			if (e.metaKey || e.ctrlKey || e.altKey) return;

			const list = rowsRef.current;
			if (!list.length) return;
			const at = list.findIndex(a => a._id === cursor);

			if (e.key === 'j' || e.key === 'k') {
				e.preventDefault();
				const next =
					e.key === 'j'
						? list[Math.min(list.length - 1, at + 1)]
						: list[Math.max(0, at <= 0 ? 0 : at - 1)];
				if (next) {
					setCursor(next._id);
					document
						.querySelector(`[data-alert-id="${next._id}"]`)
						?.scrollIntoView({ block: 'nearest' });
				}
				return;
			}
			if (e.key === 'Escape') {
				setOpenId(null);
				return;
			}
			const current = at >= 0 ? list[at] : null;
			if (!current) return;
			const txn = txnOf(current);

			if (e.key === 'Enter' && txn) {
				e.preventDefault();
				setOpenId(txn._id);
				return;
			}
			if (e.key === 'x') {
				e.preventDefault();
				toggle(current._id, !checked.has(current._id));
				return;
			}
			if (e.key === 'm') {
				e.preventDefault();
				alertAct(current._id, { assignToMe: true });
				return;
			}
			if (e.key === 'e') {
				e.preventDefault();
				alertAct(current._id, { escalate: true });
				return;
			}
			if (!txn) return;
			/* After a decision the row leaves the tab it was in, so the cursor
			   moves to the NEXT row first — otherwise it lands on nothing and the
			   next keystroke does nothing, which reads as the shortcuts breaking. */
			const after = list[Math.min(list.length - 1, at + 1)];
			if (e.key === 'a') {
				e.preventDefault();
				setCursor(after?._id ?? null);
				act(txn._id, 'APPROVED', 'analyst_approved');
			} else if (e.key === 'd') {
				e.preventDefault();
				setCursor(after?._id ?? null);
				act(txn._id, 'DECLINED', 'analyst_declined');
			} else if (e.key === 'u') {
				e.preventDefault();
				act(txn._id, 'AWAITING_USER', 'rule_change_status');
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [cursor, checked, act, alertAct, toggle]);

	const bins = stats?.bins ?? [];
	const tiles = stats?.tiles;
	const meter = stats?.hourMeter;
	const meterSegs = meter
		? ([
				['ok', meter.resolved, 'resolved'],
				['pend', meter.investigating, 'investigating'],
				['pend', meter.awaiting, 'awaiting user'],
				['rej', meter.open, 'still open'],
				['other', meter.dismissed, 'dismissed · false positive'],
			] as [string, number, string][])
		: [];

	return (
		<div className="sv2-scope sv2 txn-deck">
			<PageHead
				title="Txn Monitoring — Transaction investigate"
				sub={
					<>
						{dateLine()} · Didit KYT · every alert opened by a rule, due soonest first
						{failed && <span className="rd"> · refresh failed, showing the last good page</span>}
					</>
				}
			/>

			<div className="ctl">
				{/* The range chip is where "live" is claimed, so it is also where the
				    claim is withdrawn — a stalled header keeps showing its last good
				    numbers under a label that says they are current. */}
				<span className={`range${statsFailed ? ' stale' : ''}`}>
					{statsFailed ? 'Stale · last 60 min at last refresh' : 'Live · last 60 min'}
				</span>
				<button className="pre" type="button" onClick={() => setSort(k => (k === 'due' ? 'score' : k === 'score' ? 'new' : 'due'))}>
					Sort: <b>{SORTS[sort]}</b> ▾
				</button>
				<button
					className={`pre${sevIdx ? ' on' : ''}`}
					type="button"
					onClick={() => setSevIdx(i => (i + 1) % SEVS.length)}
				>
					Severity: <b>{SEVS[sevIdx]}</b> ▾
				</button>
				<button
					className={`pre${bundle === 'All' ? '' : ' on'}`}
					type="button"
					onClick={() =>
						setBundle(b => bundles[(bundles.indexOf(b) + 1) % bundles.length] ?? 'All')
					}
				>
					Bundle: <b>{bundle}</b> ▾
				</button>
				<button
					className={`pre${kind === 'All' ? '' : ' on'}`}
					type="button"
					title="Payment moves value one way. A trade has two legs in different currencies — sent and received."
					onClick={() =>
						setKind(k => (k === 'All' ? 'Payment' : k === 'Payment' ? 'Trade' : 'All'))
					}
				>
					Kind: <b>{kind}</b> ▾
				</button>
				{/* The catalogue has its own page. This was briefly a modal here; a
				    rule table with per-row configure and delete is a screen, not an
				    overlay on the queue, and the queue is what an analyst is
				    mid-way through when they notice a noisy rule. */}
				<button className="pre" type="button" onClick={() => navigate(ROUTES.TM_RULES)}>
					Rules <b>{rules.length || '·'}</b> →
				</button>
				<span className="rn">
					{stats ? `${stats.throughput.perMinute}/min · ${stats.throughput.transactions} in the window` : '—'}
				</span>
			</div>

			<div className="strip estrip">
				<Tile tone={2} label="Open alerts" n={tiles?.openAlerts ?? 0}
					note={tiles?.oldestOpenAt ? `oldest ${ago(new Date(tiles.oldestOpenAt).getTime())}` : 'queue clear'}
					pts={bins.map(b => b.rev + b.rej + b.run)} />
				<Tile tone={3} label="Resolved · last hour" n={tiles?.resolved ?? 0} note="closed in the window"
					pts={bins.map(b => b.ok)} />
				<Tile tone={5} label="Auto-declined by rules" n={tiles?.autoDeclined ?? 0}
					note={tiles?.autoDeclined ? 'no analyst needed' : 'none'} pts={bins.map(b => b.rej)} />
				<Tile tone={1} label="Awaiting user" n={tiles?.awaitingUser ?? 0} note="remediation open"
					pts={bins.map(b => b.run)} />
			</div>

			<div className="rangebar">
				<div className="rb-head">
					<span className="rb-cap">Alerts in the last hour</span>
					<span className="rb-total mono">{meter?.total ?? '—'}</span>
				</div>
				<div className="rb-meter" role="img" aria-label="Alerts in the last hour by status">
					{meterSegs
						.filter(([, v]) => v > 0)
						.map(([k, v, label]) => (
							<span key={label} className={`rb-seg rb-seg--${k}`} style={{ flexGrow: v }} />
						))}
				</div>
				<div className="rb-legs five">
					{meterSegs.map(([k, v, label]) => (
						<div key={label} className={`rb-leg rb-leg--${k}${v > 0 ? ' on' : ''}`}>
							<div className="rb-n mono">{v}</div>
							<div className="rb-l">{label}</div>
						</div>
					))}
				</div>
			</div>

			<div className="legend">
				rule states:
				<span className="nd fail">hard rule · +30 or sets status</span>
				<span className="nd review">adds score</span>
				<span className="nd test">test mode · no score</span>
				<span className="nd pass">no rule matched</span>
				· thresholds <span className="mono">review ≥60 · decline ≥85</span>
			</div>

			{/* SEARCH SITS ABOVE THE TABS, not in the control strip it came from.
			    The strip is about how the queue is SCOPED — severity, bundle, kind,
			    ranking — while this decides which rows are in it at all, and it
			    belongs next to the tabs and the queue it filters. Moved rather than
			    duplicated, and the same `.decksearch` row the investigate page
			    uses, so the two screens do not grow separate versions of one
			    control.

			    THE TAB COUNTS DELIBERATELY DO NOT NARROW WITH IT. They are the
			    size of each queue, which is what an analyst is deciding between
			    when they pick a tab; recomputing them per search would mean the
			    badges answered a different question from the one the tabs ask. The
			    hint beside the box reports the match count instead. */}
			<div className="decksearch">
				<label
					className="srch"
					title="Searches txnId, subject name, vendor data, counterparty, rule name and currency — including both legs of a trade, so “BTC” finds a trade that only received BTC"
				>
					<svg viewBox="0 0 24 24">
						<circle cx="11" cy="11" r="7" />
						<line x1="16.5" y1="16.5" x2="21" y2="21" />
					</svg>
					<input
						placeholder="Search by name or transaction ID"
						value={q}
						onChange={e => setQ(e.target.value)}
					/>
				</label>
				{q.trim() ? (
					<span className="hint">
						{loading
							? 'searching…'
							: `${queue?.total ?? 0} match${(queue?.total ?? 0) === 1 ? '' : 'es'} in this tab`}
						<button className="clr" type="button" onClick={() => setQ('')}>
							clear
						</button>
					</span>
				) : null}
			</div>

			<div className="tabs">
				{TABS.map(t => (
					<button
						key={t.key}
						type="button"
						className={`tab ${t.tone === 'mine' ? 'mine ' : ''}${tab === t.key ? 'on' : ''}`}
						onClick={() => setTab(t.key)}
					>
						{t.label}{' '}
						<span className={`bdg ${t.tone ?? ''}`}>
							{counts ? String(Math.min(99, (counts as any)[t.key] ?? 0)).padStart(2, '0') : '··'}
						</span>
					</button>
				))}
			</div>

			{checked.size > 0 && (
				<div className="bulkbar">
					<b>{checked.size} Selected</b>
					<span
						className="bx"
						role="button"
						tabIndex={0}
						title="Clear selection"
						onClick={() => setChecked(new Set())}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') setChecked(new Set());
						}}
					>
						✕
					</span>
					<button className="bact" type="button" onClick={() => runBulk('me')}>
						Assign to me
					</button>
					<button className="bact" type="button" onClick={() => runBulk('dismiss')}>
						Dismiss as false positive
					</button>
					<button className="bact" type="button" onClick={() => runBulk('approve')}>
						Approve
					</button>
					<button className="bact" type="button" onClick={() => runBulk('decline')}>
						Decline
					</button>
					{/* THE RECORD, for everything selected, as one document.
					    Sits with the other bulk actions rather than in the control
					    strip above because it acts on the SELECTION, and a control
					    that appears only once rows are ticked says what it will act
					    on without having to be told. */}
					<div className="dlmenu">
						<button
							className="bact"
							type="button"
							disabled={downloading || !selectedTxnIds.length}
							aria-expanded={bulkDlOpen}
							title={
								selectedTxnIds.length
									? `Download ${selectedTxnIds.length} transaction record${
										  selectedTxnIds.length === 1 ? '' : 's'
										} as one document`
									: 'None of the selected rows has a transaction to report on'
							}
							onClick={() => setBulkDlOpen(o => !o)}
						>
							{downloading ? 'Building…' : `Record (${selectedTxnIds.length}) ▾`}
						</button>
						{bulkDlOpen && !downloading && (
							<div className="dlmenu__list">
								{RECORD_FORMATS.map(f => (
									<button
										key={f.value}
										type="button"
										className="dlmenu__item"
										onClick={() => {
											setBulkDlOpen(false);
											download(selectedTxnIds, f.value);
										}}
									>
										{f.label}
									</button>
								))}
							</div>
						)}
					</div>
				</div>
			)}

			<div className="cols" style={{ marginTop: 12 }}>
				<div>
					<div className="panel">
						<div className="ph">
							Incoming <span className="r">newest transactions</span>
						</div>
						<div className="tape">
							{tape.length ? (
								tape.map(t => (
									<span
										key={t._id}
										className={`tk ${verdictOf(t.status)}`}
										title={
											`${t.txnId} · ${t.status}` +
											(t.feePreferredCurrencyAmount
												? ` · fee $${t.feePreferredCurrencyAmount.toLocaleString()}${
														t.preferredCurrencyTotalAmount
															? ` · total $${t.preferredCurrencyTotalAmount.toLocaleString()} (reported, not scored)`
															: ''
													}`
												: '')
										}
									>
										<b>{hms(new Date(t.txnDate).getTime())}</b>
										{/* A TRADE HAS NO SINGLE DIRECTION. The tape used to print the
										    top-level amount and an in/out arrow — on a trade that is the
										    SENT leg alone, with an arrow implying the whole transaction
										    moved one way and the received side simply absent. The tape is a
										    dense fixed-width strip, so a trade gets the compact slash form:
										    a slash says "exchanged" without claiming either side is
										    inbound, which for a trade is exactly the point. A payment
										    renders byte for byte as before. */}
										{t.transactionKind === 'trade' && t.legs?.sent && t.legs?.received ? (
											<>
												{money(t.legs.sent.amount, t.legs.sent.currency, t.legs.sent.currencyKind)}/
												{money(
													t.legs.received.amount,
													t.legs.received.currency,
													t.legs.received.currencyKind
												)}{' '}
											</>
										) : (
											<>
												{money(t.amount, t.currency)}
										{feeState(t) !== 'unsupplied' ? (
											<sup className="feeflag">ƒ</sup>
										) : null}{' '}
										{t.direction === 'INBOUND' ? '←' : '→'}{' '}
											</>
										)}
										<span className="sc">{t.score}</span>
									</span>
								))
							) : (
								<span className="tk">no transactions ingested yet</span>
							)}
						</div>
					</div>

					<div className="panel">
						<div className="ph">
							Alert queue
							<span className="r">
								{/* The queue used to say only "N shown", which is the same sentence
								    whether the tab holds 12 alerts or 1,200 — the page silently
								    stopped at the first 40 with nothing to say more existed. */}
								{total > rows.length
									? `${rows.length} of ${total} · ${(SORTS[sort] ?? '').toLowerCase()}`
									: `${rows.length} shown · ${(SORTS[sort] ?? '').toLowerCase()}`}
								{pages > 1 && <Pager page={pageNo} pages={pages} onChange={setPageNo} />}
							</span>
						</div>
						<div className="tscroll">
							<table className="stbl atq">
								<thead>
									<tr>
										<th className="ckcol">
											<input
												type="checkbox"
												aria-label="Select every row on this page"
												checked={rows.length > 0 && checked.size === rows.length}
												onChange={e =>
													setChecked(e.target.checked ? new Set(rows.map(a => a._id)) : new Set())
												}
											/>
										</th>
										<th className="alcol">Alert · subject</th>
										<th className="whycol">Why · matched rules</th>
										<th className="sccol">Score</th>
										<th className="duecol">Due</th>
										<th className="accol">Actions</th>
									</tr>
								</thead>
								<tbody>
									{rows.length === 0 && (
										<tr>
											<td colSpan={6} className="empty">
												{loading
													? 'Loading the queue…'
													: failed
														? 'Could not reach transaction monitoring.'
														: q.trim()
															? /* SAY WHICH IS EMPTY. "Nothing in this queue" to
															     someone who has just typed a transaction id blames
															     the queue for a miss on their needle — and on this
															     screen especially it reads as though the alert went
															     missing. */
																`Nothing in this tab matches “${q.trim()}”. Searched transaction ids, subject, counterparty, rule name and currency.`
															: 'Nothing in this queue.'}
											</td>
										</tr>
									)}
									{rows.map(a => {
										const t = txnOf(a);
										if (!t) return null;
										return (
											<tr
												key={a._id}
												data-alert-id={a._id}
												className={`${edgeOf(t.status)}${
													openId === t._id || cursor === a._id ? ' sel' : ''
												}`}
												onClick={() => {
													setCursor(a._id);
													setOpenId(t._id);
												}}
											>
												<td className="ckcol">
													<input
														type="checkbox"
														className="rowck"
														aria-label={`Select alert for ${t.subjectName}`}
														checked={checked.has(a._id)}
														onClick={e => e.stopPropagation()}
														onChange={e => toggle(a._id, e.target.checked)}
													/>
												</td>
												<td className="al alcol">
													<span className="fund">
														{a.description || 'Manual'} · alert {a._id.slice(-8)}
														{a.linkedCaseId && <span className="pu"> · {a.linkedCaseId}</span>}
													</span>
													<span className="nm">{t.subjectName}</span>
													<span className="mail">
														{t.subjectVendorData} · {t.subjectEntityType} · {t.subjectCountry}
													</span>
													<span className="sid">
														<b>txn</b> : <span className="sidv">{t.txnId}</span>
													</span>
													<div className="meta">
														<b>{t.transactionKind === 'trade' ? 'trade' : t.actionType}</b> ·{' '}
														{/* BOTH LEGS on a trade. Showing one side of a
														    BTC-for-USD conversion describes half of what
														    happened, and the fiat-equivalent alone hides
														    that crypto was involved at all. */}
														<span title={feeTitle(t)}>
															{legsLabel(t)}
															{/* Present AND explicit-zero both get the marker — a fee reported
															    as zero is a fact worth hovering. Only "no fee data at all"
															    shows nothing, so a row carrying no fees looks exactly as it did
															    before. The three figures live in the hover rather than the
															    visible text: this cell has room for one more thing, not three
															    more numbers. */}
															{feeState(t) !== 'unsupplied' ? (
																<sup className="feeflag" aria-label="fee information">
																	ƒ
																</sup>
															) : null}
														</span>{' '}
														· {corridor(t)} {t.counterpartyName}
														<br />
														<b>Created</b> : {hms(new Date(t.txnDate).getTime())} ·{' '}
														{ago(new Date(t.txnDate).getTime())} ago
													</div>
													<div className="pills">
														<StatusChip status={t.status} />
														<TagChips tags={t.tags} />
													</div>
												</td>
												<td className="whycol">
													<div className="chain">
														<RuleChips runs={t.ruleRuns} max={3} />
													</div>
													<div className="fnm">
														{t.decisionReasonLabel}
														{t.decisionReasonCode && (
															<>
																{' · '}
																<span className="mono">{t.decisionReasonCode}</span>
															</>
														)}
													</div>
												</td>
												<td className="sccol">
													<ScoreCell score={t.score} withSeverity />
												</td>
												<td className="duecol">
													<span className={`due ${dueClass(a)}`}>
														{/* An alert with no clock says so, rather than
														    formatting the epoch. See dueClass. */}
														{a.dueAt ? until(new Date(a.dueAt).getTime()) : 'no SLA'}
													</span>
													<span className={`own${a.assignedTo ? '' : ' none'}`}>
														{a.assignedTo ?? 'unassigned'}
													</span>
												</td>
												<td className="accol">
													<div className="acts">
														<button
															className="dbtn ok"
															type="button"
															onClick={e => {
																e.stopPropagation();
																act(t._id, 'APPROVED', 'analyst_approved');
															}}
														>
															Approve
														</button>
														<button
															className="dbtn danger"
															type="button"
															onClick={e => {
																e.stopPropagation();
																act(t._id, 'DECLINED', 'analyst_declined');
															}}
														>
															Decline
														</button>
														<span
															role="presentation"
															onClick={e => e.stopPropagation()}
															onKeyDown={e => e.stopPropagation()}
														>
															<Kebab
																items={[
																	{ label: 'Assign to me', onClick: () => alertAct(a._id, { assignToMe: true }) },
																	{
																		label: 'Request user info',
																		onClick: () => act(t._id, 'AWAITING_USER', 'rule_change_status'),
																	},
																	{ label: 'Escalate to case', onClick: () => alertAct(a._id, { escalate: true }) },
																	{
																		label: 'Dismiss (false positive)',
																		onClick: () => act(t._id, 'APPROVED', 'analyst_cleared'),
																	},
																]}
															/>
														</span>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>

					<div className="foot">
						<span className="kb"><kbd>j</kbd><kbd>k</kbd> move</span>
						<span className="kb"><kbd>enter</kbd> open</span>
						<span className="kb"><kbd>a</kbd> approve</span>
						<span className="kb"><kbd>d</kbd> decline</span>
						<span className="kb"><kbd>u</kbd> request user info</span>
						<span className="kb"><kbd>e</kbd> escalate</span>
						<span className="kb"><kbd>m</kbd> assign to me</span>
						<span className="kb"><kbd>x</kbd> select</span>
						<br />
						Rows are alerts, not transactions — an approved transaction never appears here. The coloured
						left edge is the transaction status; the due clock is the alert&apos;s <code>dueAt</code> and
						turns amber at a quarter of the SLA left, red once breached. A decision here is a{' '}
						<code>PATCH /transactions/:id</code> against transaction.monitoring.api, which records who
						decided and when on the transaction&apos;s own activity.
					</div>
				</div>

				<div>
					<div className="panel">
						<div className="ph">Rules firing <span className="r">last 60 min</span></div>
						{stats?.rulesFiring?.length ? (
							stats.rulesFiring.map(r => {
								const max = stats.rulesFiring[0]?.count || 1;
								return (
									<div className="rulebar" key={r.ruleName} title={r.ruleName}>
										<span className="rl">{r.ruleName}</span>
										<span className="rb">
											<i style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
										</span>
										<span className="rc">{r.count}</span>
									</div>
								);
							})
						) : (
							<Empty>No rules fired in the last hour.</Empty>
						)}
					</div>

					<div className="panel">
						<div className="ph">Queue</div>
						<table className="kv">
							<tbody>
								<tr><td>Open</td><td className="rd">{counts?.open ?? '—'}</td></tr>
								<tr><td>Investigating</td><td className="a">{counts?.investigating ?? '—'}</td></tr>
								<tr><td>Awaiting user</td><td className="bl">{counts?.awaiting ?? '—'}</td></tr>
								<tr><td>Pending SAR</td><td className="rd">{counts?.sar ?? '—'}</td></tr>
								<tr><td>Resolved</td><td className="g">{counts?.done ?? '—'}</td></tr>
							</tbody>
						</table>
					</div>

					<div className="panel">
						<div className="ph">Throughput</div>
						<table className="kv">
							<tbody>
								<tr><td>transactions · 1h</td><td className="bl">{stats?.throughput.transactions ?? '—'}</td></tr>
								<tr><td>per minute</td><td className="bl">{stats?.throughput.perMinute ?? '—'}</td></tr>
								<tr><td>auto-declined</td><td className="rd">{tiles?.autoDeclined ?? '—'}</td></tr>
								<tr>
									<td>window</td>
									<td className="gy">{stats ? `${stats.window.minutes} min` : '—'}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<TxnDrawer id={openId} onClose={() => setOpenId(null)} onChanged={reload} />
		</div>
	);
};

export default TriageDeck;
